import { useTranslation } from 'react-i18next'

import {
  Button,
  DatePicker,
  EditIcon,
  Input,
  Label,
  PinIcon,
  Select,
  Text,
} from '@mercai/clever'

import { GeneralInformationProps } from './general-information.type'

import {
  SupplierPaymentPriorityEnum,
  SupplyOrderShipmentStatusEnum,
} from '@/types'
import { SectionHeader } from '../section-header'
import styles from './general-information.module.css'

export const GeneralInformation = ({
  errors,
  values,
  setValues,
  onShowAddSupplier,
  isQuotation,
  position,
}: GeneralInformationProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementComponents')

  return (
    <SectionHeader
      title={`${position}. ${t('componentGeneralInformationTitle')}`}
    >
      <div className={styles['info-block']}>
        <div className={styles.title}>
          <PinIcon className={styles.icon} />
          <Text variant="title-medium">
            {t('componentGeneralInformationSupplierTitle')}
            <span className={styles.required}>*</span>
          </Text>
        </div>
        <div className={styles['info-row']}>
          {values?.supplier && (
            <div className={styles['info-row-content']}>
              <Text className={styles['info-item']}>
                <strong>
                  {t('componentGeneralInformationSupplierNameLabel')}:
                </strong>{' '}
                {values?.supplier?.name || '-'}
              </Text>
              <Text className={styles['info-item']}>
                <strong>
                  {t('componentGeneralInformationSupplierEmailLabel')}:
                </strong>{' '}
                {values?.supplier?.email || '-'}
              </Text>
              <Text className={styles['info-item']}>
                <strong>
                  {t('componentGeneralInformationSupplierPhoneLabel')}:
                </strong>{' '}
                {values?.supplier?.phone || '-'}
              </Text>
            </div>
          )}

          {!values?.supplier && (
            <Text className={styles['info-row-content']}>
              {t('componentGeneralInformationSupplierNoSelected')}
            </Text>
          )}

          {values?.supplier ? (
            <Button
              variant="secondary"
              model="square"
              size="small"
              onClick={onShowAddSupplier}
            >
              <EditIcon />
            </Button>
          ) : (
            <Button variant="secondary" onClick={onShowAddSupplier}>
              {t('componentGeneralInformationSupplierAddButton')}
            </Button>
          )}
        </div>

        {!!errors?.supplierUuid && (
          <Text variant="text-base" className={styles.error}>
            {errors.supplierUuid as string}
          </Text>
        )}
      </div>

      {!isQuotation && (
        <div className={styles['info-block']}>
          <div className={styles['info-row-content']}>
            <div>
              <Label required>
                {t('componentGeneralInformationPurchaseOrderCodeLabel')}
              </Label>
              <Input
                placeholder={t(
                  'componentGeneralInformationPurchaseOrderCodePlaceholder',
                )}
                value={values?.purchaseOrderCode}
                onChange={(val) =>
                  setValues((prev) => ({
                    ...prev,
                    purchaseOrderCode: val || '',
                  }))
                }
                className={styles.input}
                error={errors?.purchaseOrderCode as string}
              />
            </div>

            <div>
              <Label>
                {t('componentGeneralInformationExpectedPaymentDateLabel')}
              </Label>
              <DatePicker
                value={values?.expectedPaymentDate}
                onChange={(val) =>
                  setValues((prev) => ({ ...prev, expectedPaymentDate: val }))
                }
                placeholder="DD/MM/YYYY"
                className={styles.input}
              />
            </div>
            <div>
              <Label>
                {t('componentGeneralInformationPaymentPriorityLabel')}
              </Label>
              <Select
                value={values?.paymentPriority}
                options={Object.keys(SupplierPaymentPriorityEnum).map(
                  (key) => ({
                    label: tEnums(`supply_payment_priority_${key}`),
                    value: key,
                  }),
                )}
                onChange={(val) =>
                  setValues((prev) => ({
                    ...prev,
                    paymentPriority:
                      val as keyof typeof SupplierPaymentPriorityEnum,
                  }))
                }
                placeholder={t('componentGeneralInformationPlaceholder')}
                className={styles.input}
              />
            </div>
            <div>
              <Label>
                {t('componentGeneralInformationShipmentStatusLabel')}
              </Label>
              <Select
                value={values?.shipmentStatus}
                options={Object.keys(SupplyOrderShipmentStatusEnum).map(
                  (key) => ({
                    label: tEnums(`supply_order_shipment_status_${key}`),
                    value: key,
                  }),
                )}
                onChange={(val) =>
                  setValues((prev) => ({
                    ...prev,
                    shipmentStatus:
                      val as keyof typeof SupplyOrderShipmentStatusEnum,
                  }))
                }
                placeholder={t('componentGeneralInformationPlaceholder')}
                className={styles.input}
              />
            </div>
          </div>
        </div>
      )}
    </SectionHeader>
  )
}
